import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, useTheme } from '@mui/material';
import { NumberParam, type QueryParamConfigMap, StringParam } from 'serialize-query-params';
import { useToggle } from 'usehooks-ts';

import {
  GetUserOrganizationAssignmentsParams,
  useGetUserOrganizationAssignmentsInfinite,
  UserOrganizationAssignmentDTO
} from '@/api/generated';
import PlusIcon from '@/assets/icons/plus.svg?react';
import DataGrid from '@/components/DataGrid/DataGrid';
import Field from '@/components/fields/Field';
import SearchInput from '@/components/fields/SearchInput';
import Form from '@/components/Form/Form';
import StickyHead from '@/components/StickyHead/StickyHead';
import { usePlatformPermissions } from '@/hooks/permissions/usePlatformPermissions';
import { DEFAULT_PAGINATION, DEFAULT_SORTING, useEntityList } from '@/hooks/useEntityList';
import { UserSectionHiddenFields } from '@/views/Filters/constants';
import FilterButton from '@/views/Filters/FilterButton';
import FilterPanel from '@/views/Filters/FilterPanel';
import SectionUser from '@/views/Filters/SectionUser';
import InviteClientUsersModal from '@/views/Users/InviteClientUsersModal';

import { clientUserColumns } from './userColumns';

const DEFAULT_VALUES = {
  query: '',
  ...DEFAULT_SORTING,
  ...DEFAULT_PAGINATION
};

const URL_CONFIG_MAP = {
  query: StringParam,
  size: NumberParam,
  'user.organization': StringParam
} satisfies QueryParamConfigMap;

const HIDDEN_FIELDS = new Set<UserSectionHiddenFields>([UserSectionHiddenFields.ROLE]);

type ClientUsersFormFields = GetUserOrganizationAssignmentsParams & {
  user?: {
    organization?: string;
  };
};

const ClientUsers = () => {
  const { spacing } = useTheme();
  const { t } = useTranslation(['users', 'common']);
  const [queryBody, setQueryBody] = useState<GetUserOrganizationAssignmentsParams | null>(null);
  const [showFilters, toggleShowFilters] = useToggle();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const {
    data: usersData,
    isError,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetUserOrganizationAssignmentsInfinite(queryBody ?? {}, {
    query: { suspense: false, enabled: !!queryBody }
  });

  const usersList = useMemo(
    () => usersData?.pages?.flatMap(page => page?.userOrganizationAssignments || []) || [],
    [usersData]
  );

  const handleFetchNextPage = useCallback(() => {
    if (isError || !hasNextPage || isFetchingNextPage || !usersData?.pages?.length) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isError, isFetchingNextPage, usersData?.pages?.length]);

  const getQueryBody = useCallback(
    (query: ClientUsersFormFields): GetUserOrganizationAssignmentsParams => ({
      query: query.query,
      organization_id: query?.user?.organization,
      page: query.page,
      size: query.size,
      sort: query.sort,
      direction: query.direction
    }),
    []
  );
  const { sorting, handleChangeSort, form } = useEntityList<
    ClientUsersFormFields,
    GetUserOrganizationAssignmentsParams
  >(DEFAULT_VALUES, URL_CONFIG_MAP, setQueryBody, getQueryBody, 300);

  const { value: { canCreateClientUsers, canDeleteOnboardingUsers } = {} } = usePlatformPermissions();

  const columns = useMemo(() => clientUserColumns(t, canDeleteOnboardingUsers), [t, canDeleteOnboardingUsers]);

  const getRowId = useCallback(
    (row: UserOrganizationAssignmentDTO) =>
      [row.userId, row.organization?.slug].filter((item): item is string => Boolean(item)).join('|'),
    []
  );

  const handleOpenInviteModal = useCallback(() => {
    setShowInviteModal(true);
  }, []);

  const handleCloseInviteModal = useCallback(() => {
    setShowInviteModal(false);
  }, []);

  return (
    <Box sx={{ px: 5, flexGrow: 1 }}>
      <StickyHead sx={{ pt: 2.25 }}>
        <Form form={form} className="Filters-root">
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
            <Field
              name="query"
              component={SearchInput}
              innerProps={{
                placeholder: t('users.searchPlaceholder'),
                sx: { minWidth: 320, bgcolor: 'background.default' }
              }}
            />
            <Stack direction="row" alignItems="center" gap={3}>
              <FilterButton isOpen={showFilters} onClick={toggleShowFilters} />
              {canCreateClientUsers && (
                <Button
                  variant="contained"
                  onClick={handleOpenInviteModal}
                  sx={{
                    '& .PlusIcon-root': { width: spacing(2), height: spacing(2) }
                  }}
                  startIcon={<PlusIcon className="PlusIcon-root" />}
                >
                  {t('filters:filters.new')}
                </Button>
              )}
            </Stack>
          </Stack>
          <FilterPanel isOpen={showFilters}>
            <SectionUser name="user" hiddenFields={HIDDEN_FIELDS} />
          </FilterPanel>
        </Form>
      </StickyHead>
      <DataGrid
        data={usersList}
        columns={columns}
        onSort={handleChangeSort}
        isLoading={isLoading}
        isLoadingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        sorting={sorting}
        resizeWatchSelector=".Filters-root"
        hasError={isError && !usersList.length}
        onLoadNextPage={handleFetchNextPage}
        getRowId={getRowId}
      />
      {showInviteModal && <InviteClientUsersModal onClose={handleCloseInviteModal} />}
    </Box>
  );
};

export default ClientUsers;
